/**
 * 가구 모델 라이브러리.
 *
 * 각 가구는 **치수 계약(dims)** 을 갖고, 모든 부품 위치가 거기서 파생된다.
 * 상판 두께를 바꾸면 다리 길이와 서랍 높이가 따라 움직여야 한다 — 부품을 눈대중으로
 * 밀어 맞추기 시작하면 치수를 조정할 수 없는 모델이 된다.
 *
 * 좌표: 원점은 바닥 중앙. +Y 위, -Z 가 정면(플레이어가 보는 쪽).
 */

import * as THREE from 'three';
import {
  bevelBox,
  cappedArc,
  frameMolding,
  lathe,
  moldingProfile,
  plankPanel,
  roundedRect,
  type MeshWriter,
  type Slot,
  shellBox,
} from './kernel';

export interface Piece {
  id: string;
  /** 프리셋 변형이면 원본 조각 id (variants.ts) */
  variantOf?: string;
  name: string;
  /** 컬렉션 — 게임(테마)별로 묶는다 */
  collection: 'manor' | 'sahol' | 'arch';
  category: string;
  note: string;
  dims: Record<string, number>;
  /** seed 로 치수를 흔든다(2026-09-14). 배치가 seed 를 줄 때만 불린다 — 카탈로그 시트는 기본 치수 */
  vary?: (seed: number, d: Record<string, number>) => Record<string, number>;
  /** 정점색 낡음(색조·접지 그림자·모서리)을 굽지 않는다 — 인쇄물처럼 색이 곧 정보인 것 */
  noWeather?: boolean;
  /**
   * **앞뒤가 없는 물건**임을 선언한다 — 상자·통·더미·원형 탁자·식탁처럼 사방에서 쓰거나
   * 바닥에 놓인 서류처럼 방향이 의미 없는 것. 배치 감사(`tools/audit-place.ts`)가 이 조각에는
   * 「앞이 벽을 본다」「앞 0.6m 가 막혔다」를 묻지 않는다. 없으면 발자국이 정사각형에 가까운
   * 것만 앞이 없다고 추정하는데, 그 추정이 양동이(걸레 자루 때문에 길쭉함)를 틀리게 봤다.
   */
  nofront?: boolean;
  /**
   * **이 조각이 어떻게 열리는가** (2026-09-11, Chris: 「박스는 뚜껑을 따로 해야 하잖아 …
   * 일체로 만들어서 테이프 자국 있는 거면 양 사이드로 열리고 없는 상자는 옆으로 슥 밀리게」).
   *
   * 여태는 몸통 조각 + 뚜껑 조각 + 프롭 셋을 손으로 맞춰야 했고, 상자를 옮기면 뚜껑이
   * 따로 놀았다. 이제 조각이 **제 뚜껑을 안고** 있고, 여기서 어떻게 여는지만 말한다.
   * 움직일 부품은 `build` 안에서 `w.movable('이름', () => …)` 로 감싼다.
   *
   * 여러 개를 적으면 **동시에** 움직인다 — 테이프 상자의 양쪽 플랩이 그렇다.
   */
  openable?: {
    /** `w.movable()` 에 준 이름 */
    part: string;
    /**
     * **이 부품이 담는 곳** (2026-09-11, Chris: 「상자 안에 내가 직접 물건을 또 배치하거나
     * 서랍 안에 물건을 넣을 수 있어야 함」). 조각 로컬의 빈 상자 — 에디터가 물건을 여기로
     * 끌어다 놓으면 **그 부품의 자식**이 되어, 서랍을 빼면 안의 물건이 같이 나온다.
     */
    inside?: { w: number; h: number; d: number; at: [number, number, number] };
    /** 젖힘(라디안). 상자 플랩·문짝·뚜껑 */
    swing?: number;
    /**
     * **젖힘의 회전축** (2026-09-12). 여태 늘 Z 축이었다 — 좌우로 갈리는 상자 플랩은 그게 맞지만
     * **뒤 모서리에서 앞뒤로 젖히는 뚜껑은 X 축**이다. 축이 없으니 서류 상자 뚜껑이 상자를
     * 가로질러 박힌 널빤지가 됐다(열림 컷에서 Chris 가 잡았다).
     *   'z' 좌우로 갈림 — 상자 플랩
     *   'x' 앞뒤로 젖힘 — 뚜껑·문짝 (기본값 아님. 필요하면 반드시 적는다)
     */
    axis?: 'x' | 'y' | 'z';
    /** 젖힘의 회전축(조각 로컬). 없으면 조각 원점 */
    pivot?: [number, number, number];
    /** GLB 조각: 이 부품이 되는 모델 노드 이름(블렌더 오브젝트). '*' 면 모델 통째. 절차 조각은 `w.movable` 이름으로 찾으므로 비운다 */
    node?: string;
    /** 밀림(조각 로컬). 서랍이 앞으로 빠지거나 뚜껑이 옆으로 슥 */
    slide?: [number, number, number];
    /**
     * **걷어 모임** (2026-09-17, Chris: 「블라인드는 뭔 하늘로 올라가냐」).
     * pivot 을 부품 꼭대기에 두고 부품을 scale.y 로 세로로 오므린다 — 블라인드 슬랫이
     * 헤드레일 아래에 겹쳐 모이는 모양. 값은 줄어드는 비율(0.85 = 높이의 15% 만 남는다).
     * slide 로 위를 미는 건 슬랫 다발이 통째로 천장으로 날아가서 걷은 게 아니라 띄운 게 됐다.
     */
    gather?: number;
  }[];
  /**
   * 이 조각이 **몇 덩이로 떨어져 있어도 정상인가.** 기본 1 — 부품이 전부 서로 닿아
   * 한 덩이여야 한다는 뜻이다.
   *
   * 한 조각에 여러 물건을 담는 경우만 올린다 (도자기 한 벌, 쟁반 세 벌처럼).
   * tools/joint-check.mjs 가 이 값을 넘는 조각을 실패로 잡는다 — 부품이 떨어져
   * 공중에 뜬 것을 그렇게 막는다.
   */
  clusters?: number;
  /**
   * 조각 원점(y=0) **아래로 내려가는 것이 의도**임을 선언한다. 벽에 박힌 환기구,
   * 바닥에 깔린 매트처럼 접지면 아래가 있는 물건만. 이걸 안 쓰면 여행 가방 바퀴가
   * 바닥 밑으로 묻혀 안 보이는 것 같은 실수를 게이트가 잡는다.
   */
  sunk?: boolean;
  /**
   * **천장·벽에 매달리는 물건**임을 선언한다. 선언하면 조각 원점이 걸이 지점(최상단
   * 근처)이어야 하고 몸통은 원점 아래로 내려간다 — joint-check 가 강제한다.
   *
   * 이름으로 추론하려 해봤지만 못 쓴다: 「걸이」는 소파 팔걸이·옷걸이 스탠드·무구
   * 걸이처럼 **바닥에 서는** 물건에도 들어간다(오검출 3건). 매달리는지는 사람이 안다.
   */
  hangs?: boolean;
  /**
   * **무엇에 붙는 물건인가.** 배치 감사(`tools/audit-place.ts`)가 이걸로 「있어야 할 자리」를 판정한다.
   *   floor    바닥에 선다 (기본)
   *   surface  상판·선반 위에만 있는 물건 — 바닥에 있으면 오류 (컵·장부·모니터)
   *   wall     벽에 걸린다 — 등이 벽에 닿지 않으면 오류 (시계·달력·게시판)
   *   ceiling  천장에 매달린다 — 꼭대기가 천장에 닿지 않으면 오류 (펜던트·등롱)
   * 없으면 hangs 는 wall, 나머지는 floor 로 본다. 이름으로 추론하지 않는다 — 걸이·등은 바닥에 서는 것에도 들어간다.
   */
  mount?: 'floor' | 'surface' | 'wall' | 'ceiling';
  /**
   * 이 조각을 대신 그릴 외부 모델 (`client/public/models/sahol/<이름>.glb`, 또는 `/`로 시작하는 절대 경로).
   *
   * 붙어 있으면 GLB 가 도착하는 대로 `build()` 결과를 **덮는다**(지우지는 않는다 —
   * `furniture/glb.ts` 주석 참고). 크기는 `build()` 가 만든 bbox 에 맞춰지므로
   * 치수 계약과 배치 좌표가 그대로 산다.
   */
  glb?: string;
  build(w: MeshWriter, d: Record<string, number>, s?: Record<string, string>): void;
  /** 이 가구 전용 재질 (그림이 그려진 면처럼 부품마다 텍스처가 다른 경우) */
  materials?(renderer: THREE.WebGLRenderer): Partial<Record<string, THREE.Material>>;
  /**
   * 스스로 빛을 내는 조각이 달고 다니는 광원.
   *
   * **조각마다 진짜 PointLight 를 달면 안 된다.** three 는 씬의 라이트 개수를
   * 셰이더 캐시 키에 넣어서, 촛대를 열두 개 놓는 순간 재질이 전부 다시 컴파일된다.
   * 그래서 여기 적는 것은 **정의일 뿐**이고, 실제 광원은 `shrineSet` 이 들고 있는
   * 고정 개수의 풀에서 **보는 사람과 가까운 것부터** 빌려 간다.
   *
   * `y` 는 조각 밑면에서 광원까지의 높이다. `dims` 를 받아 계산할 수 있다.
   */
  light?: {
    y: (d: Record<string, number>) => number;
    color: string;
    power: number;
    range: number;
    /** 촛불은 흔들리고 전구는 안 흔들린다 */
    flicker?: boolean;
  };
}

// --- 공용 부품 ---

/** 선반으로 깎은 다리 — 굽/몸통/목/발 구간을 가진다 */
/**
 * **수종은 배치가 정한다** (2026-09-15, Chris: 「테이블이 다 너무 동일하다」). `dims.wood` 0 호두 · 1 참나무 ·
 * 2 흑단 · 3 흰 칠. 안 주면 조각의 기본. 여섯 집이 같은 호두 책상·참나무 의자면 누구 집인지 안 읽힌다.
 */
const WOODS: Slot[] = ['walnut', 'oak', 'ebony', 'painted'];
function woodOf(d: Record<string, number>, def: Slot): Slot { return WOODS[d.wood ?? -1] ?? def; }

function turnedLeg(w: MeshWriter, height: number, thick: number, slot: Slot) {
  const r = thick / 2;
  w.add(
    lathe(
      [
        [r * 0.92, 0],
        [r * 1.0, height * 0.02],
        [r * 0.86, height * 0.06],
        [r * 0.62, height * 0.13],
        [r * 0.7, height * 0.2],
        [r * 0.66, height * 0.52],
        [r * 0.88, height * 0.6],
        [r * 0.94, height * 0.66],
        [r * 0.8, height * 0.72],
        [r * 0.84, height * 0.94],
        [r * 1.0, height],
      ],
      16,
    ),
    slot,
    0.55,
  );
}

/** 사각 테이퍼 다리 (모던한 가구용) */
function taperLeg(w: MeshWriter, height: number, top: number, bottom: number, slot: Slot) {
  const seg = 4;
  for (let i = 0; i < seg; i++) {
    const t0 = i / seg;
    const t1 = (i + 1) / seg;
    const s0 = top + (bottom - top) * t0;
    const s1 = top + (bottom - top) * t1;
    const g = bevelBox((s0 + s1) / 2, (height / seg) * 1.02, (s0 + s1) / 2, 0.003);
    g.translate(0, height * (1 - (t0 + t1) / 2), 0);
    w.add(g, slot, 0.5);
  }
}

/** 인셋 패널 문/서랍 앞판 — 테두리 프레임 + 안쪽 패널 + 몰딩 */
function insetPanel(
  w: MeshWriter,
  width: number,
  height: number,
  thick: number,
  slot: Slot,
  rail = 0.06,
) {
  // 테두리 4변
  const top = bevelBox(width, rail, thick, 0.004);
  top.translate(0, height / 2 - rail / 2, 0);
  w.add(top, slot, 0.6);
  const bot = bevelBox(width, rail, thick, 0.004);
  bot.translate(0, -height / 2 + rail / 2, 0);
  w.add(bot, slot, 0.6);
  const sideH = height - rail * 2;
  for (const s of [-1, 1]) {
    const g = bevelBox(rail, sideH, thick, 0.004);
    g.translate((s * (width - rail)) / 2, 0, 0);
    w.add(g, slot, 0.6);
  }
  // 안쪽 패널 (얇고 뒤로 물러나 있다)
  // **얼굴은 -Z 다.** 안쪽 패널은 +Z(뒤)로 물러나고 몰딩은 -Z(앞)로 나온다. 부호를
  // 반대로 두면 정면에서 밋밋한 뒷면만 보이고 인셋의 그림자 선이 책상 안쪽을 향한다 —
  // 조각 규약이 정면 -Z 이므로 헬퍼도 그쪽을 앞으로 잡아야 한다.
  const inner = bevelBox(width - rail * 2 + 0.008, sideH + 0.008, thick * 0.55, 0.003);
  inner.translate(0, 0, thick * 0.2);
  w.add(inner, slot, 0.6);
  // 패널 둘레 몰딩 — 이 그림자 선이 "판때기"와 "짜맞춘 문"을 가른다
  const prof = moldingProfile(rail * 0.5, thick * 0.5, [
    { kind: 'bead', at: 0.3, spread: 0.16, depth: 0.5 },
    { kind: 'cove', at: 0.68, spread: 0.2, depth: -0.35 },
  ]);
  const mold = frameMolding(width - rail * 2, sideH, prof);
  mold.rotateX(-Math.PI / 2);
  mold.translate(0, 0, -thick * 0.16);
  w.add(mold, slot, 0.4);
}

/** 놋쇠 손잡이 (당김쇠) */
function brassPull(w: MeshWriter, span: number) {
  const r = 0.007;
  const bar = new THREE.CylinderGeometry(r, r, span, 10);
  bar.rotateZ(Math.PI / 2);
  bar.translate(0, 0, -0.032);
  w.add(bar, 'brass', 0.25);
  for (const s of [-1, 1]) {
    const post = lathe(
      [
        [0.011, 0],
        [0.012, 0.008],
        [0.007, 0.014],
        [0.008, 0.03],
      ],
      10,
    );
    post.rotateX(-Math.PI / 2);
    post.translate((s * span) / 2, 0, 0);
    w.add(post, 'brass', 0.25);
  }
}

/** 둥근 손잡이 (문/서랍) */
function brassKnob(w: MeshWriter) {
  const k = lathe(
    [
      [0.014, 0],
      [0.016, 0.01],
      [0.009, 0.018],
      [0.021, 0.03],
      [0.023, 0.04],
      [0.014, 0.049],
      [0.0, 0.052],
    ],
    14,
  );
  // **-Z(정면)로 나와야 한다.** Rx(+90°) 는 +y 를 +Z 로 보낸다 — 그래서 문을 열면 손잡이가 안쪽 면에 있었다
  // (2026-09-15 Chris 컷). +y → −Z 는 Rx(−90°) 다. 예전 주석이 반대로 적혀 있었다.
  k.rotateX(-Math.PI / 2);
  w.add(k, 'brass', 0.2);
}

/** 상판 — 위쪽 모서리에 몰딩을 두른 판 */
function moldedTop(
  w: MeshWriter,
  width: number,
  depth: number,
  thick: number,
  slot: Slot,
) {
  const core = bevelBox(width, thick, depth, 0.005, 2);
  w.add(core, slot, 0.8);
  const prof = moldingProfile(0.022, thick * 0.9, [
    { kind: 'bead', at: 0.24, spread: 0.14, depth: 0.55 },
    { kind: 'cove', at: 0.58, spread: 0.18, depth: -0.4 },
    { kind: 'fillet', at: 0.88, spread: 0.1, depth: 0.25 },
  ]);
  const m = frameMolding(width, depth, prof);
  m.translate(0, -thick / 2, 0);
  w.add(m, slot, 0.45);
}

// --- 가구 ---

export const MANOR_PIECES: Piece[] = [
  {
    id: 'desk',
    name: '서재 책상',
    collection: 'manor',
    category: '서재',
    note: '몰딩 두른 상판, 선반 깎은 다리, 인셋 패널 서랍 3단. 근거리에서 서랍 사이 그림자 선이 보인다.',
    dims: { w: 1.52, d: 0.74, h: 0.76, topT: 0.042, legT: 0.075, apron: 0.09, drawers: 3, wood: -1, plain: 0 },
    openable: [
      { part: '서랍1', slide: [0, 0, -0.3], inside: { w: 0.36, h: 0.12, d: 0.4, at: [0.49, 0.16, 0] } },
      { part: '서랍2', slide: [0, 0, -0.3], inside: { w: 0.36, h: 0.12, d: 0.4, at: [0.49, 0.36, 0] } },
      { part: '서랍3', slide: [0, 0, -0.3], inside: { w: 0.36, h: 0.12, d: 0.4, at: [0.49, 0.56, 0] } },
    ],
    build(w, d) {
      const W = woodOf(d, 'walnut');
      const legInset = d.legT / 2 + 0.03;
      w.part('상판');
      w.at(0, d.h - d.topT / 2, 0, () => moldedTop(w, d.w, d.d, d.topT, W));

      w.part('에이프런');
      const apronY = d.h - d.topT - d.apron / 2;
      const back = bevelBox(d.w - legInset * 2, d.apron, 0.022, 0.004);
      back.translate(0, apronY, d.d / 2 - legInset);
      w.add(back, W, 0.6);
      for (const s of [-1, 1]) {
        const g = bevelBox(0.022, d.apron, d.d - legInset * 2, 0.004);
        g.translate((s * (d.w - legInset * 2)) / 2, apronY, 0);
        w.add(g, W, 0.6);
      }

      w.part('다리');
      for (const sx of [-1, 1]) {
        for (const sz of [-1, 1]) {
          w.at((sx * (d.w - legInset * 2)) / 2, 0, (sz * (d.d - legInset * 2)) / 2, () =>
            d.plain ? taperLeg(w, d.h - d.topT, d.legT, d.legT * 0.75, W) : turnedLeg(w, d.h - d.topT, d.legT, W),
          );
        }
      }

      w.part('서랍');
      const n = d.drawers;
      const bankW = 0.42;
      const dh = (d.apron * 0 + (d.h - d.topT - 0.12)) / n;
      // 서랍 앞판은 **몸통(carcass) 앞면에 물려야** 한다. -d.d/2 + 0.012 로 두면 앞판이
      // 몸통보다 71mm 앞에 떠 있었는데, 손잡이가 +Z 로 나와 우연히 그 틈을 메우고 있어서
      // joint-check 가 조용했다. 손잡이를 정면(-Z)으로 돌리자 드러났다.
      const carcassD = d.d - legInset * 2 - 0.02;
      const carcassZ = 0.01;
      const drawerZ = carcassZ - carcassD / 2 - 0.008;
      for (let i = 0; i < n; i++) {
        const y = 0.12 + dh * (i + 0.5);
        w.movable(`서랍${i + 1}`, () => w.at(d.w / 2 - bankW / 2 - legInset, y, drawerZ, () => {
          insetPanel(w, bankW - 0.02, dh - 0.012, 0.024, W, 0.045);
          // -0.02 면 post(고정 다리)가 앞판에서 7mm 떠서 손잡이가 별개 덩이가 된다
          w.at(0, 0, -0.008, () => brassPull(w, 0.14));
          // 서랍 상자 — 앞판 뒤에 붙어 같이 빠진다. 열면 속이 보여야 서랍이다(2026-09-15)
          const bw = bankW - 0.05, bh = dh - 0.03, bd = carcassD - 0.04;
          for (const g of shellBox(bw, bh, bd, 0.01, ['+y'])) { g.translate(0, -dh / 2 + 0.012, bd / 2 + 0.014); w.add(g, W, 0.5); }
          const fy = -dh / 2 + 0.012 + 0.01, fz = bd / 2 + 0.014;
          if (i === 0) {   // 맨 위: 서류 뭉치 + 펜
            const s1 = bevelBox(bw - 0.08, 0.018, bd - 0.1, 0.002); s1.translate(-0.01, fy + 0.009, fz); w.add(s1, 'paper', 0.5);
            const pen = new THREE.CylinderGeometry(0.005, 0.005, 0.14, 8); pen.rotateZ(Math.PI / 2); pen.rotateY(0.3); pen.translate(0.08, fy + 0.023, fz - 0.06); w.add(pen, 'ebony', 0.3);
          } else if (i === 1) {   // 가운데: 봉투 몇 장 + 작은 상자
            for (let k = 0; k < 3; k++) { const e = bevelBox(0.22, 0.006, 0.11, 0.001); e.rotateY(0.15 * (k - 1)); e.translate(-0.03 + k * 0.02, fy + 0.003 + k * 0.006, fz + 0.02); w.add(e, 'paper', 0.5); }
            const tin = bevelBox(0.1, 0.035, 0.07, 0.004); tin.translate(0.1, fy + 0.018, fz - 0.05); w.add(tin, 'leather', 0.5);
          } else {   // 맨 아래: 파일철 두 권 세워 두기
            for (let k = 0; k < 2; k++) { const f = bevelBox(0.03, bh - 0.03, bd - 0.1, 0.003); f.translate(-0.08 + k * 0.05, fy + (bh - 0.03) / 2, fz); w.add(f, k ? 'cardboard' : 'leather', 0.5); }
          }
        }));
      }
      // 서랍장 몸통 (서랍이 들어가는 상자)
      // 앞이 뚫린 통 — 통짜면 서랍을 빼도 속이 막힌 판이다
      for (const g of shellBox(bankW, d.h - d.topT - 0.12, carcassD, 0.014, ['-z'])) { g.translate(d.w / 2 - bankW / 2 - legInset, 0.12, carcassZ); w.add(g, W, 0.6); }

      if (d.plain) return;   // 수수한 책상엔 가죽 상판이 없다
      w.part('가죽 상판');
      const pad = bevelBox(d.w * 0.62, 0.003, d.d * 0.66, 0.002);
      pad.translate(-d.w * 0.14, d.h + 0.002, 0);
      w.add(pad, 'leather', 0.5);
    },
  },

  {
    id: 'chair',
    name: '등받이 의자',
    collection: 'manor',
    category: '서재',
    note: '세로살 등받이, 천 좌판, 테이퍼 다리와 가로대. 살 사이로 뒤가 비쳐야 의자로 읽힌다.',
    dims: { w: 0.46, d: 0.48, seatH: 0.45, backH: 0.95, legT: 0.038, slats: 5, wood: -1 },
    /* 의자를 앞으로 끌어 빼 본다 (2026-09-16) */
    openable: [{ part: '의자', slide: [0, 0, -0.15] }],
    vary: (seed, d) => { const r = (k: number) => { const v = Math.sin(seed * 12.9898 + k * 78.233) * 43758.5453; return v - Math.floor(v); }; return { ...d, seatH: d.seatH * (0.98 + r(1) * 0.04), backH: d.backH * (0.95 + r(2) * 0.1), slats: 4 + Math.floor(r(3) * 3) }; },
    build(w, d) {
      w.movable('의자', () => {
      const W = woodOf(d, 'oak');
      const inset2 = d.legT / 2 + 0.02;   // 다리/등받이가 쓰는 것과 같은 값
      w.part('좌판');
      const seat = bevelBox(d.w, 0.032, d.d, 0.006, 2);
      seat.translate(0, d.seatH, 0);
      w.add(seat, W, 0.6);
      /**
       * **방석은 등받이 살대 앞에서 끝나야 한다.** 깊이를 `d.d - 0.03` 으로 잡았더니
       * 뒤쪽 끝(z=0.225)이 등받이 기둥 앞면(z=0.184)을 지나서, 살대가 방석 위에
       * 올라선 것처럼 보였다. 뒷끝을 기둥 앞면에서 끊는다.
       */
      const backFront = (d.d - inset2 * 2) / 2 - (d.legT * 0.9) / 2;
      const cushFront = -(d.d / 2 - 0.015);
      const cushBack = backFront - 0.005;
      const cushion = bevelBox(d.w - 0.03, 0.028, cushBack - cushFront, 0.012, 2);
      cushion.translate(0, d.seatH + 0.03, (cushFront + cushBack) / 2);
      w.add(cushion, 'fabric', 0.4);

      w.part('다리');
      const inset = d.legT / 2 + 0.02;
      for (const sx of [-1, 1]) {
        for (const sz of [-1, 1]) {
          w.at((sx * (d.w - inset * 2)) / 2, 0, (sz * (d.d - inset * 2)) / 2, () =>
            taperLeg(w, d.seatH, d.legT, d.legT * 0.62, W),
          );
        }
      }

      w.part('가로대');
      for (const sz of [-1, 1]) {
        const g = bevelBox(d.w - inset * 2, 0.02, 0.018, 0.003);
        g.translate(0, d.seatH * 0.34, (sz * (d.d - inset * 2)) / 2);
        w.add(g, W, 0.4);
      }
      for (const sx of [-1, 1]) {
        const g = bevelBox(0.018, 0.02, d.d - inset * 2, 0.003);
        g.translate((sx * (d.w - inset * 2)) / 2, d.seatH * 0.34, 0);
        w.add(g, W, 0.4);
      }

      w.part('등받이');
      const postH = d.backH - d.seatH;
      for (const sx of [-1, 1]) {
        const post = bevelBox(d.legT, postH, d.legT * 0.9, 0.004);
        post.translate((sx * (d.w - inset * 2)) / 2, d.seatH + postH / 2, (d.d - inset * 2) / 2);
        w.add(post, W, 0.5);
      }
      const rail = bevelBox(d.w - inset * 2 + d.legT, 0.055, 0.026, 0.005);
      rail.translate(0, d.backH - 0.03, (d.d - inset * 2) / 2);
      w.add(rail, W, 0.5);
      /**
       * 세로살 — **두 끝을 계산해서 맞춘다.**
       * 예전에는 높이를 `postH*0.72`, 중심을 `seatH + postH*0.42` 로 어림해서 살대가
       * 0.48~0.84 에 놓였다. 윗대 밑면이 0.8925 라 위로 **5.2cm 떠 있었고**, 방석이
       * 0.466~0.494 라 아래로는 방석을 **1.4cm 뚫고** 들어갔다 — 살이 방석 위에
       * 얹힌 것처럼 보였다(2026-09-06 Chris 지적). 비율로 잡으면 dims 를 바꾸는
       * 순간 다시 어긋나므로, 아래는 방석 윗면·위는 윗대 안쪽을 **직접** 참조한다.
       */
      const inner = d.w - inset * 2 - d.legT;
      const railBottom = d.backH - 0.03 - 0.055 / 2;
      const slatBot = d.seatH + 0.016;             // 좌판 널 윗면에서 올라간다
      const slatTop = railBottom + 0.008;          // 윗대 안으로 8mm 물린다(장부)
      const slatH = slatTop - slatBot;
      for (let i = 0; i < d.slats; i++) {
        const t = (i + 0.5) / d.slats - 0.5;
        const s = bevelBox(0.016, slatH, 0.014, 0.003);
        s.translate(t * inner, (slatBot + slatTop) / 2, (d.d - inset * 2) / 2);
        w.add(s, W, 0.3);
      }
      });
    },
  },

  {
    id: 'bookshelf',
    name: '책장',
    collection: 'manor',
    category: '서재',
    note: '크라운 몰딩, 선반 5단, 시드 기반으로 꽂힌 책들. 책은 두께·높이·기울기가 제각각이다.',
    dims: { w: 1.0, d: 0.32, h: 2.1, sideT: 0.028, shelves: 5, seed: 7, wood: -1 },
    /** 칸마다 책 줄을 앞으로 당긴다 — 뒤에 숨긴 것을 찾는 동작 */
    openable: [
      { part: '1칸 책', slide: [0, 0, -0.16], inside: { w: 0.8, h: 0.2, d: 0.2, at: [0, 0.12, 0.06] } },
      { part: '2칸 책', slide: [0, 0, -0.16], inside: { w: 0.8, h: 0.2, d: 0.2, at: [0, 0.50, 0.06] } },
      { part: '3칸 책', slide: [0, 0, -0.16], inside: { w: 0.8, h: 0.2, d: 0.2, at: [0, 0.88, 0.06] } },
      { part: '4칸 책', slide: [0, 0, -0.16], inside: { w: 0.8, h: 0.2, d: 0.2, at: [0, 1.26, 0.06] } },
      { part: '5칸 책', slide: [0, 0, -0.16], inside: { w: 0.8, h: 0.2, d: 0.2, at: [0, 1.64, 0.06] } },
    ],
    build(w, d) {
      const W = woodOf(d, 'walnut');
      const inner = d.w - d.sideT * 2;
      w.part('측판');
      for (const s of [-1, 1]) {
        const g = bevelBox(d.sideT, d.h, d.d, 0.004);
        g.translate((s * (d.w - d.sideT)) / 2, d.h / 2, 0);
        w.add(g, W, 0.7);
      }
      w.part('뒷판');
      const back = bevelBox(inner, d.h - 0.1, 0.012, 0.003);
      back.translate(0, d.h / 2, d.d / 2 - 0.008);
      w.add(back, W, 0.7);

      w.part('선반');
      const gap = (d.h - 0.16) / d.shelves;
      for (let i = 0; i <= d.shelves; i++) {
        const y = 0.08 + gap * i;
        const g = bevelBox(inner, 0.024, d.d - 0.02, 0.004);
        g.translate(0, y, -0.006);
        w.add(g, W, 0.6);
      }

      w.part('크라운 몰딩');
      const prof = moldingProfile(0.05, 0.09, [
        { kind: 'crown', at: 0.5, spread: 0.3, depth: 0.5 },
        { kind: 'bead', at: 0.12, spread: 0.09, depth: 0.4 },
        { kind: 'cove', at: 0.78, spread: 0.14, depth: -0.3 },
      ]);
      const crown = frameMolding(d.w - 0.02, d.d - 0.02, prof);
      crown.translate(0, d.h - 0.09, 0);
      w.add(crown, W, 0.4);
      const base = bevelBox(d.w + 0.03, 0.08, d.d + 0.02, 0.005);
      base.translate(0, 0.04, 0);
      w.add(base, W, 0.6);

      w.part('책');
      let rnd = d.seed * 9301 + 49297;
      const rand = () => ((rnd = (rnd * 9301 + 49297) % 233280) / 233280);
      // 고서적 표지 — 채도를 낮춰야 '장난감 블록'이 아니라 책으로 읽힌다
      const covers = ['#4e2429', '#243449', '#2d4029', '#4a3d1f', '#35243d', '#42281a', '#3a3a3f'];
      for (let sh = 0; sh < d.shelves; sh++) {
        const y = 0.08 + gap * sh + 0.012;
        let x = -inner / 2 + 0.015;
        const fill = 0.55 + rand() * 0.4;
        // **칸마다 책 줄이 통째로 빠진다** (2026-09-16) — 책 뒤는 숨기기 좋은 자리다.
        // 줄 전체를 앞으로 당기는 것이라 책 한 권씩은 아니다. 칸 수만큼 부품이 생긴다
        w.movable(`${sh + 1}칸 책`, () => {
        while (x < -inner / 2 + inner * fill) {
          const bw = 0.018 + rand() * 0.026;
          const bh = gap * (0.6 + rand() * 0.26);
          const lean = rand() < 0.12 ? (rand() - 0.5) * 0.22 : 0;
          const bd = d.d * (0.62 + rand() * 0.2);
          const g = bevelBox(bw, bh, bd, 0.002);
          g.translate(0, bh / 2, 0);
          if (lean) g.rotateZ(lean);
          g.translate(x + bw / 2, y, -0.01);
          const mat = covers[Math.floor(rand() * covers.length)];
          // 책은 표지 색이 제각각이라 paint 슬롯에 색을 굽는 대신 정점 컬러를 쓴다
          paintGeometry(g, mat);
          w.add(g, 'paint', 0.35);
          x += bw + 0.002 + rand() * 0.004;
        }
        });
      }
    },
  },

  {
    id: 'nightstand',
    name: '협탁',
    collection: 'manor',
    category: '침실',
    note: '서랍 1단 + 여닫이 문, 놋쇠 손잡이. 침대 옆에서 눈높이 아래로 보이는 가구.',
    dims: { w: 0.52, d: 0.42, h: 0.62, topT: 0.032, legH: 0.11, wood: -1 },
    openable: [
      { part: '서랍', slide: [0, 0, -0.26], inside: { w: 0.4, h: 0.1, d: 0.3, at: [0, 0.45, 0] } },
      { part: '문', swing: 1.5, axis: 'y', pivot: [-0.225, 0.3, -0.194], inside: { w: 0.4, h: 0.25, d: 0.3, at: [0, 0.2, 0] } },   // 경첩 왼쪽(손잡이가 오른쪽)
    ],
    build(w, d) {
      const W = woodOf(d, 'walnut');
      w.part('상판');
      w.at(0, d.h - d.topT / 2, 0, () => moldedTop(w, d.w, d.d, d.topT, W));

      w.part('몸통');
      const bodyH = d.h - d.topT - d.legH;
      // 앞이 뚫린 통 + 서랍칸/문칸 사이 선반. 통짜면 열어도 속이 판이다(2026-09-15)
      for (const g of shellBox(d.w - 0.04, bodyH, d.d - 0.03, 0.014, ['-z'])) { g.translate(0, d.legH, 0); w.add(g, W, 0.6); }
      const drawerH = bodyH * 0.34, doorH = bodyH * 0.6;
      const mid = bevelBox(d.w - 0.07, 0.014, d.d - 0.06, 0.002); mid.translate(0, d.legH + doorH + 0.03, 0.01); w.add(mid, W, 0.6);

      w.part('서랍');
      w.movable('서랍', () => w.at(0, d.legH + bodyH - drawerH / 2 - 0.01, -d.d / 2 + 0.016, () => {
        insetPanel(w, d.w - 0.07, drawerH - 0.012, 0.022, W, 0.04);
        w.at(0, 0, -0.008, () => brassPull(w, 0.12));
        const bw = d.w - 0.1, bh = drawerH - 0.03, bd = d.d - 0.08;
        for (const g of shellBox(bw, bh, bd, 0.009, ['+y'])) { g.translate(0, -drawerH / 2 + 0.012, bd / 2 + 0.012); w.add(g, W, 0.5); }
        const fy = -drawerH / 2 + 0.021, fz = bd / 2 + 0.012;
        const book = bevelBox(0.13, 0.028, 0.19, 0.003); book.rotateY(-0.2); book.translate(-0.06, fy + 0.014, fz); w.add(book, 'leather', 0.5);   // 머리맡 책
        const note = bevelBox(0.09, 0.012, 0.13, 0.002); note.translate(0.09, fy + 0.006, fz + 0.03); w.add(note, 'paper', 0.5);
      }));

      w.part('문칸');   // 문 뒤 — 접은 수건 한 장과 병 하나
      const towel = bevelBox(d.w - 0.2, 0.04, d.d - 0.16, 0.012, 2); towel.translate(-0.05, d.legH + 0.014 + 0.02, 0.02); w.add(towel, 'fabric', 0.6);
      const bottle = lathe([[0.03, 0], [0.032, 0.02], [0.03, 0.12], [0.014, 0.15], [0.014, 0.18], [0, 0.18]], 12); bottle.translate(d.w / 2 - 0.1, d.legH + 0.014, 0.05); w.add(bottle, 'plastic', 0.4);

      w.part('문');
      w.movable('문', () => w.at(0, d.legH + doorH / 2 + 0.01, -d.d / 2 + 0.016, () => {
        insetPanel(w, d.w - 0.07, doorH - 0.012, 0.022, W, 0.05);
        w.at(d.w * 0.28, 0, -0.008, () => brassKnob(w));
      }));

      w.part('다리');
      for (const sx of [-1, 1]) {
        for (const sz of [-1, 1]) {
          w.at((sx * (d.w - 0.1)) / 2, 0, (sz * (d.d - 0.09)) / 2, () =>
            turnedLeg(w, d.legH, 0.052, W),
          );
        }
      }
    },
  },

  {
    id: 'bed',
    name: '침대',
    collection: 'manor',
    category: '침실',
    note: '패널 헤드보드, 매트리스와 눌린 이불, 베개 2개. 이불 주름이 평평한 판과 침구를 가른다.',
    dims: { w: 1.42, d: 2.02, frameH: 0.34, headH: 1.05, mattress: 0.22, wood: -1 },
    /* 베개를 들어 올려 밑을 본다 (2026-09-16) */
    openable: [{ part: '베개', slide: [0, 0, -0.25] }],
    build(w, d) {
      const W = woodOf(d, 'walnut');
      w.part('프레임');
      for (const sx of [-1, 1]) {
        const g = bevelBox(0.06, d.frameH, d.d, 0.005);
        g.translate((sx * (d.w - 0.06)) / 2, d.frameH / 2 + 0.06, 0);
        w.add(g, W, 0.7);
      }
      const foot = bevelBox(d.w, d.frameH * 0.8, 0.06, 0.005);
      foot.translate(0, d.frameH * 0.4 + 0.06, -d.d / 2 + 0.03);
      w.add(foot, W, 0.7);

      w.part('헤드보드');
      w.at(0, 0, d.d / 2 - 0.03, () => {
        const post = 0.075;
        for (const sx of [-1, 1]) {
          const g = bevelBox(post, d.headH, 0.07, 0.005);
          g.translate((sx * (d.w - post)) / 2, d.headH / 2, 0);
          w.add(g, W, 0.6);
        }
        const panelW = d.w - post * 2;
        w.at(0, d.headH * 0.6, 0, () => insetPanel(w, panelW, d.headH * 0.66, 0.045, W, 0.07));
        const cap = bevelBox(d.w + 0.03, 0.05, 0.09, 0.006);
        cap.translate(0, d.headH, 0);
        w.add(cap, W, 0.5);
      });

      w.part('매트리스');
      const m = bevelBox(d.w - 0.12, d.mattress, d.d - 0.12, 0.035, 2);
      m.translate(0, d.frameH + d.mattress / 2 + 0.02, 0);
      w.add(m, 'fabric', 0.7);

      if (!d.bare) {   // bare: 이불·베개를 GLB 조각(bedding)이 대신할 때 — 둘이 겹치면 이불이 두 겹이다(2026-09-14)
      w.part('이불');
      // 침대 발치로 갈수록 두꺼워지는 층 + 접힌 깃
      const quiltY = d.frameH + d.mattress + 0.045;
      // **박스 한 장으로 두면 note 가 경고한 「평평한 판」이 된다** — 「이불 주름이 평평한
      // 판과 침구를 가른다」고 못박혀 있다. 폭 방향으로 다섯 골을 내고 골마다 두께를
      // 달리해 눌린 결을 만든다. 폭을 4mm 겹치게 잡아 골끼리 붙는다(joint-check).
      const qw = d.w - 0.06;
      const qd = d.d * 0.62;
      const ridges = 5;
      for (let i = 0; i < ridges; i++) {
        const t = (i + 0.5) / ridges;
        const hh = 0.044 + Math.sin(t * Math.PI * 2.2) * 0.013;
        const g = bevelBox(qw / ridges + 0.004, hh, qd, 0.02, 2);
        g.translate(-qw / 2 + (qw / ridges) * (i + 0.5), quiltY + (hh - 0.05) / 2, -d.d * 0.16);
        w.add(g, 'fabric', 0.9);
      }
      const fold = bevelBox(d.w - 0.06, 0.035, 0.16, 0.02, 2);
      fold.translate(0, quiltY + 0.03, d.d * 0.15);
      w.add(fold, 'fabric', 0.5);

      w.part('베개');
      for (const sx of [-1, 1]) {
        const drawPillow = () => {
          const p = bevelBox(0.56, 0.11, 0.34, 0.05, 3);
          p.rotateX(-0.12);
          p.translate(sx * 0.32, d.frameH + d.mattress + 0.09, d.d / 2 - 0.34);
          w.add(p, 'fabric', 0.45);
        };
        // 왼쪽 베개만 들어 올릴 수 있게 뗀다
        if (sx === -1) w.movable('베개', drawPillow); else drawPillow();
      }
      }
    },
  },

  {
    id: 'wardrobe',
    name: '옷장',
    collection: 'manor',
    category: '침실',
    note: '여닫이 두 짝, 크라운과 굽. 문 사이 틈과 손잡이가 정면 실루엣을 만든다.',
    dims: { w: 1.14, d: 0.58, h: 2.06, baseH: 0.1, crownH: 0.11, wood: -1 },
    /* 기본 치수 기준 정적 경첩(w 1.14 → 짝폭 0.54, d 0.58 → 앞면 z −0.27). 좁은 변형은 경첩이 어긋난다 — 붙박이장과 같은 한계 */
    openable: [
      { part: '왼짝', swing: 1.6, axis: 'y', pivot: [-0.54, 1.1, -0.27], inside: { w: 1.0, h: 1.0, d: 0.45, at: [0, 0.6, 0] } },
      { part: '오른짝', swing: -1.6, axis: 'y', pivot: [0.54, 1.1, -0.27] },
    ],
    build(w, d) {
      const W = woodOf(d, 'walnut');
      w.part('몸통');
      const bodyH = d.h - d.baseH - d.crownH;
      for (const sx of [-1, 1]) {
        const g = bevelBox(0.03, bodyH, d.d, 0.004);
        g.translate((sx * (d.w - 0.03)) / 2, d.baseH + bodyH / 2, 0);
        w.add(g, W, 0.7);
      }
      // 뒤판은 **뒤에서 4cm 들여** 짠다 — 벽에 딱 붙이면 굽도리 몰딩(8mm+)이 얇은 뒤판을 뚫고 들어와
      // 문을 열었을 때 벽이 보였다(2026-09-15 Chris 컷). 실제 옷장도 뒤판이 들어가 있다
      const backZ = d.d / 2 - 0.04;
      const back = bevelBox(d.w - 0.06, bodyH, 0.016, 0.003);
      back.translate(0, d.baseH + bodyH / 2, backZ);
      w.add(back, W, 0.7);
      const top = bevelBox(d.w - 0.04, 0.028, d.d - 0.02, 0.004);
      top.translate(0, d.baseH + bodyH - 0.014, 0);
      w.add(top, W, 0.6);
      const floor = bevelBox(d.w - 0.06, 0.018, d.d - 0.06, 0.003);
      floor.translate(0, d.baseH + 0.009, -0.02);
      w.add(floor, W, 0.6);

      // --- 안 — 열면 옷장이어야 한다: 윗선반, 옷걸이 봉, 걸린 옷, 개어 둔 옷, 바닥 상자 ---
      w.part('선반');
      const shelfY = d.baseH + bodyH - 0.36;
      const shelf = bevelBox(d.w - 0.06, 0.02, d.d - 0.1, 0.003);
      shelf.translate(0, shelfY, -0.02);
      w.add(shelf, W, 0.6);
      w.part('봉');
      const rod = new THREE.CylinderGeometry(0.012, 0.012, d.w - 0.08, 10);
      rod.rotateZ(Math.PI / 2);
      rod.translate(0, shelfY - 0.06, -0.03);
      w.add(rod, 'steel', 0.3);
      w.part('걸린 옷');
      const cloths: Slot[] = ['fabric', 'redcloth', 'painted', 'leather', 'fabric', 'ebony'];
      const nH = Math.max(3, Math.min(6, Math.round((d.w - 0.2) / 0.16)));
      for (let i = 0; i < nH; i++) {
        const x = -(d.w - 0.3) / 2 + (i + 0.5) * ((d.w - 0.3) / nH);
        const len = 0.75 + (i % 3) * 0.1;
        const g = bevelBox(0.11 + (i % 2) * 0.03, len, 0.3 - (i % 2) * 0.06, 0.02, 2);
        g.translate(x, shelfY - 0.08 - len / 2, -0.03);
        w.add(g, cloths[i % cloths.length], 0.6);
        const hook = new THREE.CylinderGeometry(0.004, 0.004, 0.06, 6);
        hook.translate(x, shelfY - 0.06 - 0.03, -0.03);
        w.add(hook, 'steel', 0.2);
      }
      w.part('개어 둔 옷');
      for (let i = 0; i < 3; i++) {
        const g = bevelBox(0.3, 0.05, 0.28, 0.012, 2);
        g.translate(-d.w / 2 + 0.25, shelfY + 0.01 + 0.025 + i * 0.05, -0.02);
        w.add(g, cloths[(i + 1) % cloths.length], 0.6);
      }
      w.part('바닥 상자');
      const bx = bevelBox(0.34, 0.2, 0.3, 0.006);
      bx.translate(d.w / 2 - 0.25, d.baseH + 0.018 + 0.1, -0.02);
      w.add(bx, 'cardboard', 0.5);

      w.part('문');
      const doorW = (d.w - 0.06) / 2;
      for (const sx of [-1, 1]) {
        // 짝마다 움직이는 부품 — 경첩은 바깥 모서리(openable.pivot). 안에 옷을 넣을 수 있다
        w.movable(sx < 0 ? '왼짝' : '오른짝', () => w.at((sx * doorW) / 2, d.baseH + bodyH / 2, -d.d / 2 + 0.02, () => {
          insetPanel(w, doorW - 0.008, bodyH - 0.02, 0.03, W, 0.07);
          w.at(-sx * (doorW / 2 - 0.05), 0, -0.008, () => brassKnob(w));
        }));
      }

      w.part('크라운');
      const prof = moldingProfile(0.055, d.crownH, [
        { kind: 'crown', at: 0.5, spread: 0.32, depth: 0.55 },
        { kind: 'bead', at: 0.14, spread: 0.1, depth: 0.35 },
        { kind: 'cove', at: 0.8, spread: 0.15, depth: -0.32 },
      ]);
      const crown = frameMolding(d.w - 0.02, d.d - 0.02, prof);
      crown.translate(0, d.h - d.crownH, 0);
      w.add(crown, W, 0.4);

      w.part('굽');
      const base = bevelBox(d.w + 0.02, d.baseH, d.d + 0.01, 0.006);
      base.translate(0, d.baseH / 2, 0);
      w.add(base, W, 0.6);
    },
  },

  {
    id: 'dining_table',
    nofront: true,
    name: '식탁',
    collection: 'manor',
    category: '주방',
    note: '두꺼운 상판과 에이프런, 굵은 테이퍼 다리. 아래를 들여다봐도 구조가 보인다.',
    dims: { w: 1.6, d: 0.92, h: 0.75, topT: 0.05, legT: 0.09, wood: -1, turned: 0 },
    /* 식탁을 끌어 옮겨 본다 (2026-09-16) */
    openable: [{ part: '식탁', slide: [0, 0, -0.18] }],
    build(w, d) {
      w.movable('식탁', () => {
      const W = woodOf(d, 'oak');
      w.part('상판');
      w.at(0, d.h - d.topT / 2, 0, () => moldedTop(w, d.w, d.d, d.topT, W));
      w.part('에이프런');
      const inset = d.legT / 2 + 0.05;
      const ay = d.h - d.topT - 0.05;
      for (const sz of [-1, 1]) {
        const g = bevelBox(d.w - inset * 2, 0.09, 0.026, 0.004);
        g.translate(0, ay, (sz * (d.d - inset * 2)) / 2);
        w.add(g, W, 0.6);
      }
      for (const sx of [-1, 1]) {
        const g = bevelBox(0.026, 0.09, d.d - inset * 2, 0.004);
        g.translate((sx * (d.w - inset * 2)) / 2, ay, 0);
        w.add(g, W, 0.6);
      }
      w.part('다리');
      for (const sx of [-1, 1]) {
        for (const sz of [-1, 1]) {
          w.at((sx * (d.w - inset * 2)) / 2, 0, (sz * (d.d - inset * 2)) / 2, () =>
            d.turned ? turnedLeg(w, d.h - d.topT, d.legT, W) : taperLeg(w, d.h - d.topT, d.legT, d.legT * 0.7, W),
          );
        }
      }
      });
    },
  },

  {
    id: 'counter',
    name: '주방 조리대',
    collection: 'manor',
    category: '주방',
    note: '석재 상판, 하부장 문 3짝, 스테인리스 싱크. 상판 두께의 어두운 띠가 재질을 말해준다.',
    dims: { w: 2.4, d: 0.64, h: 0.9, topT: 0.045, doors: 3, toe: 0.08 },
    /** 하부장 문이 각 짝 왼쪽 변을 축으로 앞으로 열린다 — 안에 냄비·통조림을 숨길 수 있다 (2026-09-16 상호작용) */
    openable: [
      { part: '문 1', swing: 1.5, axis: 'y', pivot: [-1.18, 0.45, -0.29], inside: { w: 2.2, h: 0.68, d: 0.46, at: [0, 0.44, 0.02] } },
      { part: '문 2', swing: 1.5, axis: 'y', pivot: [-0.393, 0.45, -0.29], inside: { w: 2.2, h: 0.68, d: 0.46, at: [0, 0.44, 0.02] } },
      { part: '문 3', swing: 1.5, axis: 'y', pivot: [0.393, 0.45, -0.29], inside: { w: 2.2, h: 0.68, d: 0.46, at: [0, 0.44, 0.02] } },
    ],
    build(w, d) {
      // **싱크 구멍을 둘러 짠다.** 통판 하나로 덮으면 볼이 그 아래 숨어 위에서 봤을 때
      // 오목한 개수대가 아니라 테두리만 그려진 평평한 돌판으로 보인다 — 싱크대
      // (sink_unit)에서 똑같은 실수를 했고 여섯 방향 시트에서 둘 다 드러났다.
      w.part('석재 상판');
      const sinkX = -d.w * 0.22;
      const sinkW = 0.52;
      const sinkD = 0.4;
      const topY = d.h - d.topT / 2;
      const leftW = sinkX - sinkW / 2 + d.w / 2;
      const rightW = d.w / 2 - (sinkX + sinkW / 2);
      for (const [tw, tx] of [
        [leftW, -d.w / 2 + leftW / 2],
        [rightW, d.w / 2 - rightW / 2],
      ] as const) {
        const g = bevelBox(tw, d.topT, d.d, 0.006, 2);
        g.translate(tx, topY, 0);
        w.add(g, 'stone', 0.9);
      }
      const sideD = (d.d - sinkD) / 2;
      for (const sz of [-1, 1]) {
        const g = bevelBox(sinkW, d.topT, sideD, 0.006, 2);
        g.translate(sinkX, topY, sz * (sinkD / 2 + sideD / 2));
        w.add(g, 'stone', 0.9);
      }
      const splash = bevelBox(d.w, 0.09, 0.02, 0.004);
      splash.translate(0, d.h + 0.045, d.d / 2 - 0.01);
      w.add(splash, 'stone', 0.6);

      w.part('싱크');
      const rim = frameMolding(
        sinkW,
        sinkD,
        moldingProfile(0.018, 0.012, [{ kind: 'bead', at: 0.4, spread: 0.25, depth: 0.4 }]),
      );
      rim.translate(sinkX, d.h - 0.004, 0);
      w.add(rim, 'steel', 0.3);
      for (const [gw, gh, gd, gy, gz] of [
        [sinkW, 0.005, sinkD, d.h - 0.19, 0],
        [sinkW, 0.19, 0.006, d.h - 0.095, sinkD / 2],
        [sinkW, 0.19, 0.006, d.h - 0.095, -sinkD / 2],
      ] as [number, number, number, number, number][]) {
        const g = bevelBox(gw, gh, gd, 0.002);
        g.translate(sinkX, gy, gz);
        w.add(g, 'steel', 0.4);
      }
      for (const sx of [-1, 1]) {
        const g = bevelBox(0.006, 0.19, sinkD, 0.002);
        g.translate(sinkX + (sx * sinkW) / 2, d.h - 0.095, 0);
        w.add(g, 'steel', 0.4);
      }
      // 수전
      const spout = lathe(
        [
          [0.026, 0],
          [0.028, 0.012],
          [0.016, 0.02],
          [0.014, 0.2],
          [0.016, 0.22],
        ],
        14,
      );
      spout.translate(-d.w * 0.22, d.h, d.d / 2 - 0.09);
      w.add(spout, 'steel', 0.25);
      // 잘린 호는 끝 단면이 뚫린다 — 끝을 막은 유틸을 쓴다
      const arc = cappedArc(0.08, 0.012, Math.PI * 0.62, 18, 8);
      arc.rotateY(Math.PI / 2);
      arc.rotateZ(Math.PI * 0.19);
      arc.translate(-d.w * 0.22, d.h + 0.21, d.d / 2 - 0.09);
      w.add(arc, 'steel', 0.25);

      w.part('하부장');
      const bodyH = d.h - d.topT - d.toe;
      const body = bevelBox(d.w - 0.02, bodyH, d.d - 0.05, 0.004);
      body.translate(0, d.toe + bodyH / 2, 0.01);
      w.add(body, 'paint', 0.7);
      const toe = bevelBox(d.w - 0.06, d.toe, d.d - 0.14, 0.003);
      toe.translate(0, d.toe / 2, 0.04);
      w.add(toe, 'ebony', 0.5);

      w.part('문');
      const doorW = (d.w - 0.04) / d.doors;
      for (let i = 0; i < d.doors; i++) {
        const x = -d.w / 2 + 0.02 + doorW * (i + 0.5);
        w.movable(`문 ${i + 1}`, () => {
        w.at(x, d.toe + bodyH / 2, -d.d / 2 + 0.03, () => {
          insetPanel(w, doorW - 0.01, bodyH - 0.02, 0.022, 'walnut', 0.055);
          w.at(0, bodyH * 0.36, -0.008, () => brassPull(w, 0.16));
        });
        });
      }
    },
  },

  {
    id: 'safe',
    name: '금고',
    collection: 'manor',
    category: '창고',
    note: '두꺼운 문틀, 다이얼과 스포크 핸들, 리벳과 경첩. 다이얼 눈금이 이 모델의 초점이다.',
    dims: { w: 0.86, d: 0.72, h: 1.0, wall: 0.07, rivets: 14 },
    /** 문이 오른쪽 경첩에서 앞으로 열린다 — 다이얼·핸들은 문에 달려 같이 돈다. 안에 귀중품을 숨긴다 (2026-09-16 상호작용) */
    openable: [
      { part: '문', swing: -1.6, axis: 'y', pivot: [0.37, 0.5, -0.385], inside: { w: 0.6, h: 0.7, d: 0.5, at: [0, 0.45, 0] } },
    ],
    build(w, d) {
      w.part('본체');
      const body = bevelBox(d.w, d.h, d.d, 0.008, 2);
      body.translate(0, d.h / 2, 0);
      w.add(body, 'steel', 0.9);

      w.part('문틀');
      const prof = moldingProfile(0.03, 0.05, [
        { kind: 'fillet', at: 0.2, spread: 0.14, depth: 0.4 },
        { kind: 'cove', at: 0.7, spread: 0.2, depth: -0.3 },
      ]);
      const frame = frameMolding(d.w - 0.1, d.h - 0.1, prof);
      frame.rotateX(-Math.PI / 2);
      frame.translate(0, d.h / 2, -d.d / 2 - 0.004);
      w.add(frame, 'steel', 0.4);

      // 문과 그 위에 붙은 모든 것 — 다이얼·핸들·경첩 쪽이 아닌 금속기는 문짝과 한 몸이다
      w.movable('문', () => {
      w.part('문');
      const door = bevelBox(d.w - 0.12, d.h - 0.12, 0.05, 0.006, 2);
      door.translate(0, d.h / 2, -d.d / 2 - 0.025);
      w.add(door, 'steel', 0.8);

      w.part('다이얼');
      const cz = -d.d / 2 - 0.05;
      const plate = lathe(
        [
          [0.105, 0],
          [0.108, 0.006],
          [0.1, 0.012],
        ],
        28,
      );
      plate.rotateX(Math.PI / 2);
      plate.translate(0, d.h * 0.56, cz);
      w.add(plate, 'brass', 0.25);
      const dial = lathe(
        [
          [0.072, 0],
          [0.076, 0.008],
          [0.07, 0.026],
          [0.05, 0.034],
          [0.0, 0.038],
        ],
        26,
      );
      dial.rotateX(Math.PI / 2);
      dial.translate(0, d.h * 0.56, cz - 0.012);
      w.add(dial, 'brass', 0.25);
      // 눈금 — 이 작은 반복이 "금고"를 읽히게 한다
      for (let i = 0; i < 24; i++) {
        const a = (i / 24) * Math.PI * 2;
        const long = i % 6 === 0;
        const t = bevelBox(0.004, long ? 0.018 : 0.011, 0.004, 0.001);
        t.translate(0, 0.088, 0);
        t.rotateZ(a);
        t.translate(0, d.h * 0.56, cz - 0.014);
        w.add(t, 'ebony', 0.1);
      }
      const pointer = bevelBox(0.006, 0.026, 0.006, 0.001);
      pointer.translate(0, d.h * 0.56 + 0.12, cz - 0.014);
      w.add(pointer, 'ebony', 0.1);

      w.part('핸들');
      const hub = lathe(
        [
          [0.03, 0],
          [0.034, 0.01],
          [0.026, 0.04],
        ],
        16,
      );
      hub.rotateX(Math.PI / 2);
      hub.translate(0, d.h * 0.32, cz - 0.02);
      w.add(hub, 'steel', 0.25);
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2;
        const spoke = new THREE.CylinderGeometry(0.011, 0.011, 0.13, 8);
        spoke.translate(0, 0.065, 0);
        spoke.rotateZ(a);
        spoke.translate(0, d.h * 0.32, cz - 0.03);
        w.add(spoke, 'steel', 0.25);
        const knobEnd = new THREE.SphereGeometry(0.017, 10, 8);
        knobEnd.translate(0, 0.13, 0);
        knobEnd.rotateZ(a);
        knobEnd.translate(0, d.h * 0.32, cz - 0.03);
        w.add(knobEnd, 'steel', 0.25);
      }
      });

      w.part('경첩');
      for (const sy of [0.22, 0.78]) {
        const h = lathe(
          [
            [0.024, 0],
            [0.026, 0.012],
            [0.024, 0.07],
            [0.02, 0.078],
          ],
          12,
        );
        h.translate(d.w / 2 - 0.035, d.h * sy - 0.039, -d.d / 2 - 0.02);
        w.add(h, 'steel', 0.2);
      }

      w.part('리벳');
      for (let i = 0; i < d.rivets; i++) {
        const t = (i + 0.5) / d.rivets;
        for (const sx of [-1, 1]) {
          const r = new THREE.SphereGeometry(0.008, 8, 6);
          r.translate((sx * (d.w - 0.03)) / 2, 0.04 + t * (d.h - 0.08), -d.d / 2 - 0.002);
          w.add(r, 'steel', 0.1);
        }
      }
    },
  },

  {
    id: 'shelf_rack',
    name: '창고 선반',
    collection: 'manor',
    category: '창고',
    note: 'ㄱ자 앵글 기둥과 타공 선반 4단. 금속 골조라 그림자가 성기게 떨어진다.',
    dims: { w: 1.1, d: 0.45, h: 1.9, shelves: 4, angle: 0.035 },
    /* 맨 아랫칸 잡동사니 상자를 꺼내 본다 (2026-09-16) */
    openable: [{ part: '상자', slide: [0, 0.05, -0.22] }],
    vary: (seed, d) => { const r = (k: number) => { const v = Math.sin(seed * 12.9898 + k * 78.233) * 43758.5453; return v - Math.floor(v); }; return { ...d, shelves: 3 + Math.floor(r(1) * 3), angle: d.angle * (0.6 + r(2) * 0.9) }; },
    build(w, d) {
      w.part('기둥');
      for (const sx of [-1, 1]) {
        for (const sz of [-1, 1]) {
          // ㄱ자 앵글 = 얇은 판 두 장
          w.at((sx * (d.w - d.angle)) / 2, 0, (sz * (d.d - d.angle)) / 2, () => {
            const a = bevelBox(d.angle, d.h, 0.005, 0.002);
            a.translate(0, d.h / 2, 0);
            w.add(a, 'steel', 0.5);
            const b = bevelBox(0.005, d.h, d.angle, 0.002);
            b.translate(0, d.h / 2, 0);
            w.add(b, 'steel', 0.5);
          });
        }
      }
      w.part('선반');
      const gap = (d.h - 0.16) / (d.shelves - 1);
      for (let i = 0; i < d.shelves; i++) {
        const y = 0.12 + gap * i;
        const g = bevelBox(d.w - d.angle, 0.018, d.d - d.angle, 0.003);
        g.translate(0, y, 0);
        w.add(g, 'steel', 0.7);
        // 앞뒤 보강 리브
        for (const sz of [-1, 1]) {
          const rib = bevelBox(d.w - d.angle, 0.028, 0.006, 0.002);
          rib.translate(0, y - 0.02, (sz * (d.d - d.angle)) / 2);
          w.add(rib, 'steel', 0.3);
        }
      }
      // 맨 아랫단 위에 놓인 골판지 상자 — 선반은 빈 것보다 뭐가 올라 있어야 창고다
      w.movable('상자', () => {
        const bx = bevelBox(0.3, 0.2, 0.3, 0.008, 2);
        bx.translate(0.18, 0.12 + 0.009 + 0.1, -0.02);   // 첫 단(중심 0.12, 판두께 0.018) 윗면에 앉는다
        paintGeometry(bx, '#9a8663');
        w.add(bx, 'paint', 0.6);
      });
    },
  },

  {
    id: 'crate',
    nofront: true,
    name: '나무 상자',
    collection: 'manor',
    category: '창고',
    note: '널판 다섯 장을 대고 모서리 각목으로 잡은 상자. 판 사이 틈이 근거리 디테일.',
    dims: { s: 0.62, plankT: 0.016, planks: 5, post: 0.03 },
    /** 상판이 뒤쪽 모서리를 축으로 젖혀진다 — 상자를 E 로 여는 동작. 안에 물건을 숨길 수 있다 (2026-09-16 상호작용) */
    openable: [
      { part: '상판', swing: 1.9, axis: 'x', pivot: [0, 0.604, 0.31], inside: { w: 0.55, h: 0.56, d: 0.55, at: [0, 0.29, 0] } },
    ],
    vary: (seed, d) => { const r = (k: number) => { const v = Math.sin(seed * 12.9898 + k * 78.233) * 43758.5453; return v - Math.floor(v); }; return { ...d, s: d.s * (0.92 + r(1) * 0.16), planks: 4 + Math.floor(r(2) * 3), plankT: d.plankT * (0.9 + r(3) * 0.2) }; },
    build(w, d) {
      const half = d.s / 2;
      w.part('널판');
      for (const [rotY, z] of [
        [0, -half],
        [Math.PI, half],
        [Math.PI / 2, 0],
        [-Math.PI / 2, 0],
      ] as [number, number][]) {
        w.at(
          rotY === Math.PI / 2 ? -half : rotY === -Math.PI / 2 ? half : 0,
          d.s / 2,
          rotY === 0 || rotY === Math.PI ? z : 0,
          () => {
            for (const g of plankPanel(d.s - d.post, d.s, d.plankT, d.planks, 0.005)) {
              g.rotateZ(Math.PI / 2);
              w.add(g, 'oak', 0.35);
            }
          },
          rotY,
        );
      }
      w.movable('상판', () => {
      w.part('상판');
      for (const g of plankPanel(d.s - d.post, d.s - d.post, d.plankT, d.planks, 0.005)) {
        g.rotateX(-Math.PI / 2);
        g.translate(0, d.s - d.plankT / 2, 0);
        w.add(g, 'oak', 0.35);
      }
      });
      w.part('모서리 각목');
      for (const sx of [-1, 1]) {
        for (const sz of [-1, 1]) {
          const g = bevelBox(d.post, d.s, d.post, 0.003);
          g.translate((sx * (d.s - d.post)) / 2, d.s / 2, (sz * (d.s - d.post)) / 2);
          w.add(g, 'oak', 0.3);
        }
      }
      w.part('못');
      for (const sx of [-1, 1]) {
        for (let i = 0; i < d.planks; i++) {
          const y = (d.s / d.planks) * (i + 0.5);
          const n = new THREE.SphereGeometry(0.004, 6, 5);
          n.translate((sx * (d.s - d.post * 1.6)) / 2, y, -half - d.plankT / 2);
          w.add(n, 'steel', 0.1);
        }
      }
    },
  },

  {
    id: 'round_table',
    nofront: true,
    name: '원형 탁자',
    collection: 'manor',
    category: '거실',
    note: '한 기둥에서 세 갈래로 뻗은 다리. 상판 테두리에 몰딩을 둘렀다.',
    dims: { r: 0.56, h: 0.74, topT: 0.04, legs: 3, wood: -1 },
    /* 탁자를 끌어 옮겨 본다 (2026-09-16) */
    openable: [{ part: '원형 탁자', slide: [0.15, 0, -0.12] }],
    build(w, d) {
      w.movable('원형 탁자', () => {
      const W = woodOf(d, 'walnut');
      w.part('상판');
      const top = new THREE.CylinderGeometry(d.r, d.r, d.topT, 44, 1);
      top.translate(0, d.h - d.topT / 2, 0);
      w.add(top, W, 0.8);
      const edge = new THREE.TorusGeometry(d.r - 0.008, d.topT * 0.42, 10, 48);
      edge.rotateX(Math.PI / 2);
      edge.translate(0, d.h - d.topT / 2, 0);
      w.add(edge, W, 0.4);

      w.part('기둥');
      // 기둥 프로파일은 기본 높이(0.74) 기준 절대값이었다 — 낮은 원탁(h 0.45)에서 상판을 뚫었다. 기둥 길이에 비례시킨다
      const k = (d.h - d.topT - 0.12 - 0.12) / (0.74 - 0.04 - 0.12 - 0.12);
      const col = lathe(
        [
          [0.06, 0],
          [0.075, 0.03 * k],
          [0.055, 0.08 * k],
          [0.048, 0.3 * k],
          [0.07, 0.42 * k],
          [0.05, 0.5 * k],
          [0.055, d.h - d.topT - 0.14],
          [0.12, d.h - d.topT - 0.12],
        ],
        22,
      );
      col.translate(0, 0.12, 0);   // 0.12 올리므로 기둥 꼭대기는 상판 밑면(d.h - d.topT)에 닿는다 — 위로 뚫고 나오면 solver 가 그 높이를 상판으로 잡는다
      w.add(col, W, 0.5);

      w.part('다리');
      for (let i = 0; i < d.legs; i++) {
        const a = (i / d.legs) * Math.PI * 2;
        const leg = bevelBox(0.055, 0.1, d.r * 0.82, 0.008, 2);
        leg.translate(0, 0.05, d.r * 0.36);
        leg.rotateY(a);
        w.add(leg, W, 0.4);
        const foot = lathe(
          [
            [0.035, 0],
            [0.04, 0.014],
            [0.028, 0.03],
          ],
          12,
        );
        foot.translate(0, 0, d.r * 0.72);
        foot.rotateY(a);
        w.add(foot, W, 0.2);
      }
      });
    },
  },

  {
    id: 'pendant',
    mount: 'ceiling',
    name: '펜던트 조명',
    collection: 'manor',
    category: '조명',
    note: '천장 캐노피에서 내려온 로드와 갓, 안쪽 전구. 갓 안쪽이 밝게 보여야 등으로 읽힌다.',
    /**
     * **천장에 매달리는 물건이라 원점이 캐노피(천장 부착면)다.** 갓과 로드는 원점
     * 아래로 내려간다 — 그래서 y 가 음수다.
     */
    hangs: true,
    sunk: true,
    dims: { shadeR: 0.24, shadeH: 0.2, rodH: 0.42, canopyR: 0.06 },
    /* 갓을 툭 건드리면 캐노피(천장 부착점)를 축으로 조금 흔들린다 (2026-09-16) */
    openable: [{ part: '조명', swing: 0.16, axis: 'x', pivot: [0, 0, 0] }],
    materials() {
      return {
        // 전구는 **발광해야** 한다. note 가 「갓 안쪽이 밝게 보여야 등으로 읽힌다」고
        // 요구하는데 glass 슬롯은 빛을 내지 않아서, 아래에서 올려봐도 갓 안이 어둡기만
        // 했다(여섯 방향 시트의 bottom 컷). 갓(paint)은 이미 양면이라 안쪽 면은 그려진다 —
        // 없던 것은 「빛」이다.
        bulb: new THREE.MeshStandardMaterial({
          color: '#fff4d6',
          roughness: 0.3,
          metalness: 0,
          emissive: new THREE.Color('#ffd9a0'),
          emissiveIntensity: 1.8,
        }),
      };
    },
    build(w, d) {
      // 조각 원점을 **캐노피**(천장 부착면)로 잡는다. 원래는 갓 하단이 y=0 이어서
      // 카탈로그에서 갓이 바닥에 앉고 로드가 위로 솟은 「삿갓 + 막대」로 보였다 —
      // 매달린 조명으로 읽히지 않는다. 맵에 놓을 때도 천장 높이를 그대로 pos.y 에
      // 적을 수 있어 이쪽이 맞다(등롱과 같은 관례).
      w.movable('조명', () => {
      w.at(0, -(d.rodH + d.shadeH), 0, () => {
      w.part('캐노피');
      const cap = lathe(
        [
          [d.canopyR, 0],
          [d.canopyR * 0.95, 0.018],
          [0.012, 0.03],
        ],
        18,
      );
      cap.translate(0, d.rodH + d.shadeH, 0);
      w.add(cap, 'brass', 0.2);

      w.part('로드');
      const rod = new THREE.CylinderGeometry(0.008, 0.008, d.rodH, 10);
      rod.translate(0, d.shadeH + d.rodH / 2, 0);
      w.add(rod, 'brass', 0.2);

      w.part('갓');
      // 프로파일은 아래에서 위로. 순서를 뒤집으면 면이 안쪽을 향해 부피가 음수가 된다.
      const shade = lathe(
        [
          [d.shadeR * 0.99, 0],
          [d.shadeR, 0.012],
          [d.shadeR * 0.55, d.shadeH * 0.5],
          [0.05, d.shadeH * 0.94],
          [0.03, d.shadeH],
        ],
        30,
      );
      // 갓은 얇은 껍질이라 아래에서 보면 안쪽이 보여야 한다.
      // 뒤집은 복사본을 겹치면 캡까지 뒤집혀 부피가 음수가 되므로, 재질을 양면으로 그린다.
      w.add(shade, 'paint', 0.5);

      w.part('소켓/전구');
      const socket = lathe(
        [
          [0.018, d.shadeH * 0.62],
          [0.02, d.shadeH * 0.72],
          [0.016, d.shadeH * 0.86],
        ],
        12,
      );
      w.add(socket, 'brass', 0.15);
      const bulb = lathe(
        [
          [0.014, d.shadeH * 0.4],
          [0.03, d.shadeH * 0.5],
          [0.032, d.shadeH * 0.6],
          [0.02, d.shadeH * 0.66],
        ],
        14,
      );
      w.add(bulb, 'bulb', 0.15);
      });
      });
    },
  },
];

/** 지오메트리에 색을 굽는다 (책 표지처럼 같은 슬롯인데 색만 다른 경우) */
function paintGeometry(geo: THREE.BufferGeometry, hex: string) {
  const c = new THREE.Color(hex);
  const n = geo.attributes.position.count;
  const arr = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    arr[i * 3] = c.r;
    arr[i * 3 + 1] = c.g;
    arr[i * 3 + 2] = c.b;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(arr, 3));
}

/** 지오메트리에 색을 굽는 유틸은 신당 가구에서도 쓴다 */
export { paintGeometry, turnedLeg, taperLeg, insetPanel, brassPull, brassKnob, moldedTop, roundedRect };
